'use client';

import { Link as HeroLink } from '@heroui/react';
import NextLink from 'next/link';
import { ReactNode } from 'react';

export interface LinkProps {
  children: ReactNode;
  onPress?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  title?: string;
  color?:
    | 'foreground'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger';
  underline?: 'none' | 'hover' | 'always';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold' | 'black';
  font?: 'bangers' | 'outfit' | 'sans';
}

const colorClasses: Record<string, string> = {
  foreground: 'text-foreground',
  primary: 'text-primary',
  secondary: 'text-secondary',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

const underlineClasses: Record<string, string> = {
  none: 'no-underline',
  hover: 'no-underline hover:underline',
  always: 'underline',
};

const fontClasses: Record<string, string> = {
  bangers: 'font-bangers',
  outfit: 'font-outfit',
  sans: 'font-sans',
};

const weightClasses: Record<string, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  black: 'font-black',
};

export const Link = ({
  children,
  onPress,
  href,
  target,
  rel,
  title,
  color = 'foreground',
  underline = 'none',
  weight = 'medium',
  font = 'outfit',
}: LinkProps) => {
  const cn = [
    'transition-all hover:opacity-80 cursor-pointer',
    colorClasses[color],
    underlineClasses[underline],
    fontClasses[font],
    weightClasses[weight],
  ].join(' ');

  if (href) {
    // If external link or explicit target, use regular anchor
    if (href.startsWith('http') || target === '_blank') {
      return (
        <a href={href} target={target} rel={rel} title={title} className={cn}>
          {children}
        </a>
      );
    }

    return (
      <NextLink href={href} target={target} rel={rel} title={title} className={cn}>
        {children}
      </NextLink>
    );
  }

  return (
    <HeroLink onPress={onPress} className={cn}>
      <span>{children}</span>
    </HeroLink>
  );
};
